import { supabase } from '../config/supabase';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';

interface TelemetryEvent {
  event_name: string;
  user_id?: string | null;
  session_id: string;
  path: string;
  meta?: Record<string, any>;
  created_at: string;
}

interface UserTraits {
  email?: string;
  name?: string;
  plan?: string;
  [key: string]: any;
}

class TelemetryService {
  private buffer: TelemetryEvent[] = [];
  private batchSize = 10;
  private flushInterval = 10000; // 10s
  private timer: NodeJS.Timeout | null = null;
  private sessionId: string;
  private debug = false;

  // Attribution Data
  private utmParams: Record<string, string> = {};
  private referrer: string = '';
  private landingPage: string = '';

  constructor() {
    this.sessionId = this.getSessionId();
    
    // Initialize External SDKs
    this.initSentry();
    this.initPostHog();

    this.captureAttribution();
    this.startFlushTimer();
    
    if (typeof window !== 'undefined') {
      // Auto-track page views (filtered)
      this.setupNavigationTracking();
      
      // Flush on close
      window.addEventListener('beforeunload', () => this.flush(true));
    }
  }

  private initSentry() {
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(),
        ],
        tracesSampleRate: 0.1, // 10%
        replaysSessionSampleRate: 0.1, // 10%
        replaysOnErrorSampleRate: 1.0, // 100% on error
      });
    }
  }

  private initPostHog() {
    if (import.meta.env.VITE_POSTHOG_KEY) {
      posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
        api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com',
        loaded: (ph) => {
          if (this.debug) console.log('PostHog loaded');
        },
        capture_pageview: false // We handle this manually
      });
    }
  }

  private getSessionId(): string {
    if (typeof window === 'undefined') return 'server-side';
    let sid = localStorage.getItem('overpixel_session_id');
    if (!sid) {
      sid = crypto.randomUUID();
      localStorage.setItem('overpixel_session_id', sid);
    }
    return sid;
  }

  private captureAttribution() {
    if (typeof window === 'undefined') return;

    // Só captura se for o início da sessão ou se tiver params novos
    const params = new URLSearchParams(window.location.search);
    const utms = ['utm_source', 'utm_campaign', 'utm_medium', 'utm_content', 'utm_term'];
    
    let hasNewAttribution = false;
    utms.forEach(key => {
      const val = params.get(key);
      if (val) {
        this.utmParams[key] = val;
        sessionStorage.setItem(`overpixel_${key}`, val);
        hasNewAttribution = true;
      } else {
        // Recupera da sessão se não tiver na URL
        const stored = sessionStorage.getItem(`overpixel_${key}`);
        if (stored) this.utmParams[key] = stored;
      }
    });

    // Landing Page & Referrer (apenas na primeira carga da sessão)
    if (!sessionStorage.getItem('overpixel_landing_page')) {
      this.landingPage = window.location.pathname;
      this.referrer = document.referrer;
      sessionStorage.setItem('overpixel_landing_page', this.landingPage);
      sessionStorage.setItem('overpixel_referrer', this.referrer);
      hasNewAttribution = true;
    } else {
      this.landingPage = sessionStorage.getItem('overpixel_landing_page') || '';
      this.referrer = sessionStorage.getItem('overpixel_referrer') || '';
    }

    if (hasNewAttribution) {
      this.track('session_start', { ...this.utmParams, referrer: this.referrer, landing_page: this.landingPage });
    }
  }

  private setupNavigationTracking() {
    this.trackPageView(); // Initial view
    
    // History API patching
    const originalPushState = history.pushState;
    history.pushState = (...args) => {
      originalPushState.apply(history, args);
      this.trackPageView();
    };

    const originalReplaceState = history.replaceState;
    history.replaceState = (...args) => {
      originalReplaceState.apply(history, args);
      this.trackPageView();
    };

    window.addEventListener('popstate', () => this.trackPageView());
  }

  private startFlushTimer() {
    if (this.timer) clearInterval(this.timer);
    this.timer = setInterval(() => this.flush(), this.flushInterval);
  }

  // --- Core Tracking Methods ---

  public async track(eventName: string, meta: Record<string, any> = {}) {
    const user = await supabase.auth.getUser();
    const props = { ...meta, ...this.utmParams };
    
    // 1. PostHog (Product Analytics)
    if (typeof window !== 'undefined' && import.meta.env.VITE_POSTHOG_KEY) {
      posthog.capture(eventName, props);
    }

    // 2. Internal Supabase Telemetry (Backup/Raw Data)
    const event: TelemetryEvent = {
      event_name: eventName,
      user_id: user.data.user?.id || null,
      session_id: this.sessionId,
      path: window.location.pathname,
      meta: props, // Always attach attribution context
      created_at: new Date().toISOString()
    };

    this.buffer.push(event);
    if (this.debug) console.log(`[Telemetry] Buffered: ${eventName}`, event);

    if (this.buffer.length >= this.batchSize) {
      this.flush();
    }
  }

  public error(error: any, context?: Record<string, any>) {
    console.error(error);
    
    // Sentry (Error Tracking)
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.captureException(error, { extra: context });
    }
  }

  public timing(name: string, value: number, props?: Record<string, any>) {
    // Log timing to PostHog as a specialized event or property
    this.track('metric_timing', { metric: name, value_ms: value, ...props });
    
    // Could also send to Sentry as a transaction span if needed
  }

  // --- Business Specific Methods ---

  public trackPageView() {
    // Ignorar rotas irrelevantes ou muito frequentes se necessário
    const props = {
      title: document.title,
      referrer: document.referrer
    };

    // PostHog PageView
    if (typeof window !== 'undefined' && import.meta.env.VITE_POSTHOG_KEY) {
      posthog.capture('$pageview', props);
    }

    // Internal Track
    this.track('page_view', props);
  }

  public async identify() {
    const user = await supabase.auth.getUser();
    if (!user.data.user) return;
    const userId = user.data.user.id;
    const email = user.data.user.email;

    // 1. PostHog Identify
    if (typeof window !== 'undefined' && import.meta.env.VITE_POSTHOG_KEY) {
      posthog.identify(userId, { email });
    }

    // 2. Sentry Identify
    if (import.meta.env.VITE_SENTRY_DSN) {
      Sentry.setUser({ id: userId, email });
    }

    // 3. Internal Identify
    // Envia dados de atribuição para a tabela user_attribution
    try {
      await supabase.rpc('identify_user_attribution', {
        p_utm_source: this.utmParams.utm_source || null,
        p_utm_campaign: this.utmParams.utm_campaign || null,
        p_utm_medium: this.utmParams.utm_medium || null,
        p_utm_content: this.utmParams.utm_content || null,
        p_utm_term: this.utmParams.utm_term || null,
        p_referrer: this.referrer || null,
        p_landing_page: this.landingPage || null
      });
      
      this.track('user_identified', { email });
    } catch (err) {
      console.error('[Telemetry] Identify failed', err);
    }
  }

  public trackModuleUsed(module: string, action: string, success: boolean, tokensUsed: number, timeMs: number) {
    this.track('module_used', {
      module,
      action,
      success,
      tokens_used: tokensUsed,
      processing_time_ms: timeMs
    });
  }

  public trackCheckoutStep(step: 'open' | 'select_plan' | 'payment_start' | 'success' | 'failed', plan?: string, value?: number) {
    this.track(`checkout_${step}`, {
      plan,
      value
    });
  }

  // --- Internals ---

  public async flush(isUrgent = false) {
    if (this.buffer.length === 0) return;

    const eventsToSend = [...this.buffer];
    this.buffer = [];

    // Se for urgente (fechando aba), tentar usar navigator.sendBeacon se possível (mas Supabase RPC precisa de fetch)
    // Para Supabase, vamos de fetch normal mesmo
    
    try {
      // Renamed to 'sync_app_events' to avoid ad-blockers blocking 'telemetry' or 'ingest' keywords
      // Explicitly adding apikey header to avoid 401 errors
      const { error } = await supabase.rpc('sync_app_events', {
        events: eventsToSend
      }, {
        // @ts-ignore
        headers: {
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY
        }
      });

      if (error) {
        if (this.debug) console.error('[Telemetry] Flush error:', error);
        // Em caso de falha, recupera eventos (se não for urgente/unload)
        if (!isUrgent) {
          this.buffer = [...eventsToSend, ...this.buffer];
        }
      } else {
        if (this.debug) console.log(`[Telemetry] Flushed ${eventsToSend.length} events`);
      }
    } catch (err) {
      console.error('[Telemetry] Flush exception:', err);
    }
  }
}

export const telemetry = new TelemetryService();
